import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { XMarkIcon } from '@heroicons/react/24/outline';

const JobForm = ({ job, onClose }) => {
  const [formData, setFormData] = useState({
    positionId: '',
    title: '',
    url: '',
    city: '',
    site: '',
    postingDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [positions, setPositions] = useState([]);
  const [sites, setSites] = useState([]);
  const [isNewSite, setIsNewSite] = useState(false);
  const [isAddingPosition, setIsAddingPosition] = useState(false);
  const [newPositionName, setNewPositionName] = useState('');
  const [positionSaving, setPositionSaving] = useState(false);

  useEffect(() => {
    fetchPositions();
    fetchSites();
    if (job) {
      setFormData({
        positionId: job.positionId || '',
        title: job.title || '',
        url: job.url || '',
        city: job.city || '',
        site: job.site || '',
        postingDate: job.postingDate || ''
      });
    } else {
      setFormData({
        positionId: '',
        title: '',
        url: '',
        city: '',
        site: '',
        postingDate: ''
      });
    }
    setIsAddingPosition(false);
    setNewPositionName('');
  }, [job]);

  const fetchPositions = async () => {
    try {
      const response = await axios.get('/api/jobs/positions');
      setPositions(response.data);
    } catch (err) {}
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get('/api/jobs/sites');
      setSites(response.data);
      if (job && response.data.includes(job.site)) {
        setIsNewSite(false);
      } else if (job && job.site) {
        setIsNewSite(true);
      }
    } catch (err) {
      setIsNewSite(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSiteSelect = (e) => {
    const value = e.target.value;
    if (value === '__new__') {
      setIsNewSite(true);
      setFormData(prev => ({ ...prev, site: '' }));
    } else {
      setFormData(prev => ({ ...prev, site: value }));
    }
  };

  const handleAddPosition = async () => {
    const trimmedName = newPositionName.trim();
    if (!trimmedName) {
      setError('Position name is required');
      return;
    }

    try {
      setError('');
      setPositionSaving(true);
      const response = await axios.post('/api/jobs/positions', { name: trimmedName });
      const createdPosition = response.data;
      const nextPositions = [...positions];
      const alreadyExists = nextPositions.some((position) => position.id === createdPosition.id);

      if (!alreadyExists) {
        nextPositions.push(createdPosition);
        nextPositions.sort((a, b) => a.name.localeCompare(b.name));
        setPositions(nextPositions);
      }

      setFormData((prev) => ({ ...prev, positionId: createdPosition.id }));
      setIsAddingPosition(false);
      setNewPositionName('');
    } catch (saveError) {
      setError(saveError.response?.data?.message || 'Error creating position');
    } finally {
      setPositionSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = { ...formData };
      if (!payload.city) delete payload.city;
      if (!payload.site) delete payload.site;
      if (!payload.postingDate) delete payload.postingDate;

      if (job) {
        await axios.put(`/api/jobs/${job.id}`, payload);
      } else {
        await axios.post('/api/jobs', payload);
      }
      onClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Error saving job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {job ? 'Edit Job' : 'Add Job'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position <span className="text-red-500">*</span>
              </label>
              <select
                name="positionId"
                value={formData.positionId}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a position</option>
                {positions.map((position) => (
                  <option key={position.id} value={position.id}>{position.name}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Choose an existing position or add a new one to the master list.
              </p>
              {!isAddingPosition ? (
                <button
                  type="button"
                  onClick={() => {
                    setError('');
                    setIsAddingPosition(true);
                  }}
                  className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  + Add Position
                </button>
              ) : (
                <div className="mt-3 flex gap-2">
                  <input
                    type="text"
                    value={newPositionName}
                    onChange={(e) => setNewPositionName(e.target.value)}
                    placeholder="e.g. Data Analyst"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddPosition}
                    disabled={positionSaving}
                    className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {positionSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddingPosition(false);
                      setNewPositionName('');
                    }}
                    disabled={positionSaving}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g. Senior Backend Developer"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              name="url"
              required
              value={formData.url}
              onChange={handleChange}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
              {!isNewSite && sites.length > 0 ? (
                <select
                  value={formData.site}
                  onChange={handleSiteSelect}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a site</option>
                  {sites.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                  <option value="__new__">+ Add new site</option>
                </select>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="site"
                    value={formData.site}
                    onChange={handleChange}
                    placeholder="e.g. LinkedIn, Indeed"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  {sites.length > 0 && (
                    <button
                      type="button"
                      onClick={() => { setIsNewSite(false); setFormData(prev => ({ ...prev, site: '' })); }}
                      className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Posting Date</label>
            <input
              type="date"
              name="postingDate"
              value={formData.postingDate}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : job ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobForm;
